import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Finding, Severity } from '../../shared/types.js';
import { AlertIcon, CheckCircleIcon, CloseIcon, InfoIcon } from './icons.js';

/* ── Pills ────────────────────────────────────────────────────────── */

type Tone = 'green' | 'amber' | 'red' | 'blue' | 'gray';

/**
 * Colour a status string the way the design does, by what the word means.
 * Red is tested first so "unpaid" and "unsatisfactory" are not read as their
 * opposites, and word boundaries keep "paid" out of "unpaid".
 */
export function toneFor(status: string): Tone {
  const s = status.toLowerCase();
  if (/conflict|unpaid|overdue|ground|gated|locked|declin|cancel|expired|restricted|unsat|over policy/.test(s)) return 'red';
  if (/\bpaid\b|confirm|complete|airworthy|best fit|monitor|approved|satisfactory|available/.test(s)) return 'green';
  if (/review|await|hold|pending|request|tight|\bopen\b|sent|defer|next up|repeat|last opening/.test(s)) return 'amber';
  if (/stage|day vfr|authorized|dual|planned|solo|checkride|rental/.test(s)) return 'blue';
  return 'gray';
}

/** Status values arrive as lowercase enum members; show them as words. */
function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function Pill({ children, tone }: { children: ReactNode; tone?: Tone }): JSX.Element {
  const text = String(children ?? '');
  const resolved = tone ?? toneFor(text);
  return <span className={`pill pill--${resolved}`}>{typeof children === 'string' ? titleCase(children) : children}</span>;
}

/* ── Findings ─────────────────────────────────────────────────────── */

export function SeverityDot({ severity }: { severity: Severity }): JSX.Element {
  return <span className={`finding__dot sev-${severity}`} />;
}

export function FindingRow({ finding, onAction }: { finding: Finding; onAction?: (href: string) => void }): JSX.Element {
  return (
    <div className="finding">
      <SeverityDot severity={finding.severity} />
      <span style={{ minWidth: 0 }}>
        <span className="finding__kind">{finding.kind}</span>
        <span className="finding__title">{finding.title}</span>
        <span className="finding__body">{finding.body}</span>
        <span className="finding__ref">{finding.reference}</span>
        {finding.action && (
          <button className="btn btn--link" onClick={() => onAction?.(finding.action!.href)}>
            {finding.action.label}
          </button>
        )}
      </span>
    </div>
  );
}

export function FindingList({ findings, onAction }: { findings: Finding[]; onAction?: (href: string) => void }): JSX.Element {
  if (!findings.length) {
    return <div className="empty">Nothing needs review.</div>;
  }
  return <>{findings.map((f, i) => <FindingRow key={`${f.code}-${i}`} finding={f} onAction={onAction} />)}</>;
}

/* ── Toasts ───────────────────────────────────────────────────────── */

interface Toast { id: number; title: string; body?: string; kind: 'ok' | 'error' | 'info' }

interface ToastApi {
  ok: (title: string, body?: string) => void;
  error: (title: string, body?: string) => void;
  info: (title: string, body?: string) => void;
}

const ToastContext = createContext<ToastApi>({ ok: () => {}, error: () => {}, info: () => {} });

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((kind: Toast['kind'], title: string, body?: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, title, body, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === 'error' ? 8000 : 4000);
  }, []);

  const value = useMemo<ToastApi>(() => ({
    ok: (t, b) => push('ok', t, b),
    error: (t, b) => push('error', t, b),
    info: (t, b) => push('info', t, b),
  }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => {
          const Glyph = t.kind === 'ok' ? CheckCircleIcon : t.kind === 'error' ? AlertIcon : InfoIcon;
          return (
            <div key={t.id} className={`toast toast--${t.kind}`}>
              <Glyph className="toast__glyph" />
              <div style={{ minWidth: 0 }}>
                <div className="toast__title">{t.title}</div>
                {t.body && <div className="muted" style={{ marginTop: 1 }}>{t.body}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = (): ToastApi => useContext(ToastContext);

/* ── Drawer ───────────────────────────────────────────────────────── */

/**
 * A centred modal. Content is laid out in columns by wrapping it in `Panes`;
 * on a phone the modal becomes a bottom sheet and the columns stack.
 */
export function Drawer({ title, subtitle, onClose, children, footer, narrow }: {
  title: string; subtitle?: string; onClose: () => void;
  children: ReactNode; footer?: ReactNode;
  /** For a single short form, where full width would look empty. */
  narrow?: boolean;
}): JSX.Element {
  // Escape dismisses, and the page behind stops scrolling while it is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  /**
   * Rendered at the top of the document rather than where it was written. A
   * drawer opened from inside another one — closing a lesson out from the
   * reservation it belongs to — would otherwise be positioned against, and
   * clipped by, the scrolling body it sits in, and simply never appear.
   */
  return createPortal((
    <div className="drawer__scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside
        className={narrow ? 'drawer drawer--narrow' : 'drawer'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <span className="drawer__grabber" />
        <div className="drawer__head">
          <div style={{ minWidth: 0 }}>
            <div className="drawer__title">{title}</div>
            {subtitle && <div className="small muted" style={{ marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button className="btn btn--icon" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>
        <div className="drawer__body">{children}</div>
        {footer && <div className="drawer__foot">{footer}</div>}
      </aside>
    </div>
  ), document.body);
}

/** A modal's sections, stacked so its information reads down the card. */
export function Panes({ children }: { children: ReactNode }): JSX.Element {
  return <div className="panes">{children}</div>;
}

/** One section of a card. `flush` lets key/value rows keep their own gutter. */
export function Pane({ title, children, flush }: {
  title?: ReactNode; children: ReactNode; flush?: boolean;
}): JSX.Element {
  return (
    <section className="pane">
      {title && <div className="pane__head">{title}</div>}
      <div className={flush ? 'pane__body pane__body--flush' : 'pane__body'}>{children}</div>
    </section>
  );
}

/* ── Small helpers ────────────────────────────────────────────────── */

export function Spinner({ label = 'Loading…' }: { label?: string }): JSX.Element {
  return <div className="spinner" role="status" aria-live="polite">{label}</div>;
}

export function ErrorNote({ message }: { message: string }): JSX.Element {
  return (
    <div className="card" style={{ margin: 12 }}>
      <div className="finding">
        <SeverityDot severity="conflict" />
        <span>
          <span className="finding__kind">Could not load</span>
          <span className="finding__title">{message}</span>
        </span>
      </div>
    </div>
  );
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }): JSX.Element {
  return (
    <div className="stat">
      <div className="stat__k">{label}</div>
      <div className="stat__v">{value}</div>
      {sub && <div className="stat__sub">{sub}</div>}
    </div>
  );
}
